import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ColumnDef } from '@tanstack/react-table'
import { PackagePlus, MoreHorizontal, Pencil, Eye, EyeOff, Trash2, Lock, Search, Plus, Network } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DataTable } from '@/components/data/DataTable'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import { CategorySelect } from '../components/CategorySelect'
import { useTemplateAccess } from '../hooks/useTemplateAccess'
import { useCan } from '@/hooks/useCan'
import { templatesApi, type Template } from '../api'

/**
 * Admin screen for the template library, and the only way into authoring one. Shipped templates
 * (IsSystem) are VaultSentinel's. In a customer's environment they can be previewed and hidden
 * from the palette, never changed. Where template authoring is on (VaultSentinel's own environment),
 * they are drawn and changed like any other. Customer templates have full CRUD, with the structure
 * drawn on the template canvas
 * (/templates/new, /templates/:id/edit) — template objects drawn from classifications, not taken from real objects.
 * Save-as-template on the planning canvas remains as the shortcut for capturing a stack that
 * already exists. Server enforces the templates.* permission set; route is admin-guarded.
 *
 * Preview is the same canvas, read-only (/templates/:id). A row opens whichever of the two this
 * person can have: the editor for a template they may change here (templates.update, and for a
 * shipped one, authoring), the preview otherwise. The menu offers only what the server would accept from them — an item
 * that can only end in a 403 is not an option.
 */
export function TemplateListPage() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const access = useTemplateAccess()
    const { canEdit, canChange, canRemove } = access
    // The screen is open to every templates.read; a new template is offered only with templates.create.
    const canCreate = useCan('templates.create')

    const [editing, setEditing] = useState<Template | null>(null)
    const [form, setForm] = useState({ Name: '', Category: '', Description: '' })

    const { data: rows = [], isLoading } = useQuery({
        queryKey: ['templates', 'admin'],
        queryFn: async () => (await templatesApi.adminList()).data.data,
    })

    const invalidate = () => queryClient.invalidateQueries({ queryKey: ['templates'] })
    const onError = (e: unknown) =>
        toast({ title: t('common.error', 'Error'), description: handleApiError(e), variant: 'destructive' })

    const saveMutation = useMutation({
        mutationFn: async () => templatesApi.update(editing!.id, {
            Name: form.Name,
            Category: form.Category,
            Description: form.Description || null,
        }),
        onSuccess: () => {
            invalidate(); setEditing(null)
            toast({ title: t('common.success', 'Success'), description: t('templates.saved', 'Template saved.') })
        },
        onError,
    })
    const publishMutation = useMutation({
        mutationFn: ({ id, published }: { id: number; published: boolean }) => templatesApi.setPublished(id, published),
        onSuccess: invalidate,
        onError,
    })
    const deleteMutation = useMutation({
        mutationFn: (id: number) => templatesApi.remove(id),
        onSuccess: () => {
            invalidate()
            toast({ title: t('common.success', 'Success'), description: t('templates.deleted', 'Template deleted.') })
        },
        onError,
    })

    const openEdit = (tpl: Template) => {
        setEditing(tpl)
        setForm({ Name: tpl.Name, Category: tpl.Category, Description: tpl.Description ?? '' })
    }

    /** The editor for a template this person may change; the read-only preview for any other. */
    const openTemplate = (tpl: Template) =>
        navigate(canChange(tpl) ? `/templates/${tpl.id}/edit` : `/templates/${tpl.id}`)

    const columns: ColumnDef<Template>[] = useMemo(() => [
        {
            accessorKey: 'Name',
            header: t('templates.name', 'Name'),
            cell: ({ row }) => (
                <span className="flex items-center gap-2">
                    <span className={row.original.Published ? '' : 'text-muted-foreground'}>
                        {row.original.Name}
                    </span>
                    {!!row.original.IsSystem && (
                        <Lock className="h-3 w-3 text-muted-foreground" aria-label={t('templates.system', 'Shipped template')} />
                    )}
                </span>
            ),
        },
        { accessorKey: 'Category', header: t('templates.category', 'Category') },
        {
            id: 'objectCount',
            header: t('templates.objectCount', 'Objects'),
            cell: ({ row }) => row.original.objects?.length ?? 0,
        },
        {
            accessorKey: 'Published',
            header: t('templates.status', 'Status'),
            cell: ({ row }) => row.original.Published
                ? <Badge variant="outline">{t('templates.published', 'In palette')}</Badge>
                : <Badge variant="secondary">{t('templates.unpublished', 'Hidden')}</Badge>,
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => {
                const tpl = row.original
                return (
                    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0" aria-label={t('common.openMenu', 'Open menu')}>
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/templates/${tpl.id}`)}>
                                    <Search className="mr-2 h-4 w-4" />
                                    {t('templates.preview', 'Preview')}
                                </DropdownMenuItem>
                                {canChange(tpl) && (
                                    <DropdownMenuItem onClick={() => navigate(`/templates/${tpl.id}/edit`)}>
                                        <Network className="mr-2 h-4 w-4" />
                                        {t('templates.editStructure', 'Edit template')}
                                    </DropdownMenuItem>
                                )}
                                {canChange(tpl) && (
                                    <DropdownMenuItem onClick={() => openEdit(tpl)}>
                                        <Pencil className="mr-2 h-4 w-4" />
                                        {t('templates.editDetails', 'Edit name and description')}
                                    </DropdownMenuItem>
                                )}
                                {canEdit && (
                                    <DropdownMenuItem onClick={() => publishMutation.mutate({ id: tpl.id, published: !tpl.Published })}>
                                        {tpl.Published
                                            ? (<><EyeOff className="mr-2 h-4 w-4" />{t('templates.hide', 'Hide from palette')}</>)
                                            : (<><Eye className="mr-2 h-4 w-4" />{t('templates.show', 'Show in palette')}</>)}
                                    </DropdownMenuItem>
                                )}
                                {canRemove(tpl) && (
                                    <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(tpl.id)}>
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        {t('common.delete', 'Delete')}
                                    </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )
            },
        },
    // The mutations' mutate and navigate are stable; the permissions are not — they arrive after
    // the first render, and a memo keyed on t alone would keep the menu they arrived too late for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t, canEdit, canChange, canRemove])

    return (
        <div className="space-y-4 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <PackagePlus className="h-5 w-5 text-primary" />
                    <div>
                        <h1 className="text-xl font-semibold">{t('templates.title', 'Templates')}</h1>
                        <p className="text-xs text-muted-foreground">
                            {t('templates.subtitle', 'Reusable stack fragments for the planning canvas. Create new ones by selecting connected objects on a visualization and choosing Save as template.')}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <Button size="sm" onClick={() => navigate('/templates/new')}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('templates.newTemplate', 'New template')}
                    </Button>
                )}
            </div>

            {/* Where shipped templates are drawn, say so: here they open for editing, and a
                customer's environment shows them read-only. */}
            {access.authoring && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                    {t('templates.authoringOn', 'Template authoring is on in this environment: shipped templates can be changed here, and new templates can be saved as shipped. Customers see shipped templates read-only.')}
                </p>
            )}

            {access.status === 'failed' && (
                <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    {t('templates.permissionsUnavailable', 'Your permissions could not be loaded, so templates open read-only. Reload the page to try again.')}
                </p>
            )}

            <DataTable
                columns={columns}
                data={rows}
                searchKey="Name"
                searchPlaceholder={t('templates.search', 'Search templates…')}
                className={isLoading ? 'opacity-60' : ''}
                // Not clickable until the permissions are known: which page a row opens depends on
                // them, and guessing would send an editor to the preview or the reverse. Once
                // known — or known to be unavailable, which the note above says — rows open.
                onRowClick={access.status === 'checking' ? undefined : openTemplate}
            />

            {editing && (
                <>
                    <div className="fixed inset-0 z-[199] bg-black/20" onClick={() => setEditing(null)} aria-hidden />
                    <div className="fixed left-1/2 top-1/2 z-[200] w-[420px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 space-y-4 rounded-lg border bg-background p-4 shadow-xl">
                        <h2 className="text-sm font-semibold">{t('templates.editTitle', 'Edit Template')}</h2>
                        <div className="space-y-1.5">
                            <Label htmlFor="tpl-edit-name">{t('templates.name', 'Name')} *</Label>
                            <Input id="tpl-edit-name" value={form.Name} onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))} maxLength={100} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="tpl-edit-cat">{t('templates.category', 'Category')}</Label>
                            <CategorySelect
                                id="tpl-edit-cat"
                                className="w-full"
                                value={form.Category}
                                options={rows.map((r) => r.Category)}
                                onChange={(Category) => setForm((f) => ({ ...f, Category }))}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="tpl-edit-desc">{t('templates.description', 'Description')}</Label>
                            <Textarea id="tpl-edit-desc" rows={3} value={form.Description} onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))} maxLength={1000} />
                        </div>
                        <div className="flex justify-end gap-2 border-t pt-3">
                            <Button variant="outline" onClick={() => setEditing(null)} disabled={saveMutation.isPending}>
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button onClick={() => saveMutation.mutate()} disabled={!form.Name.trim() || saveMutation.isPending}>
                                {saveMutation.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
